import { useEffect, useState } from "react"
import { adminApi, type Order } from "@/lib/admin-api"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table"
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog"
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select"
import { Search, ShoppingCart, Phone, User, Calendar, Package, MapPin, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

const statusLabels: Record<Order["status"], string> = {
    pending: "Kutilmoqda",
    confirmed: "Tasdiqlangan",
    delivered: "Yetkazilgan",
    cancelled: "Bekor qilingan"
}

const statusColors: Record<Order["status"], string> = {
    pending: "bg-amber-500",
    confirmed: "bg-blue-500",
    delivered: "bg-emerald-500",
    cancelled: "bg-red-500"
}

export default function AdminOrders() {
    const [orders, setOrders] = useState<Order[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState("")
    const [statusFilter, setStatusFilter] = useState<string>("all")
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
    const [isUpdating, setIsUpdating] = useState(false)

    const fetchOrders = async () => {
        try {
            setIsLoading(true)
            const data = await adminApi.getOrders()
            setOrders(data)
        } catch (error) {
            toast.error("Buyurtmalarni yuklashda xatolik")
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchOrders()
    }, [])

    const handleStatusChange = async (orderId: number, newStatus: Order["status"]) => {
        setIsUpdating(true)
        try {
            await adminApi.updateOrderStatus(orderId, newStatus)
            toast.success("Status yangilandi")
            fetchOrders()
            if (selectedOrder?.id === orderId) {
                setSelectedOrder({ ...selectedOrder, status: newStatus })
            }
        } catch (error) {
            toast.error("Xatolik yuz berdi")
        } finally {
            setIsUpdating(false)
        }
    }

    const formatPrice = (price: string | number) => {
        return new Intl.NumberFormat("uz-UZ").format(Number(price)) + " so'm"
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString("uz-UZ", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        })
    }

    const filteredOrders = orders.filter(order => {
        const matchesSearch =
            order.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.phone_number.includes(searchQuery) ||
            order.product_name.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesStatus = statusFilter === "all" || order.status === statusFilter
        return matchesSearch && matchesStatus
    })

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold">Buyurtmalar</h1>
                <p className="text-muted-foreground">Jami {orders.length} ta buyurtma</p>
            </div>

            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                            <Input
                                placeholder="Mijoz, telefon yoki mahsulot qidirish..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-12 h-12 bg-slate-50 dark:bg-gray-800 border-0"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-[180px] h-12">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Barchasi</SelectItem>
                                <SelectItem value="pending">Kutilmoqda</SelectItem>
                                <SelectItem value="confirmed">Tasdiqlangan</SelectItem>
                                <SelectItem value="delivered">Yetkazilgan</SelectItem>
                                <SelectItem value="cancelled">Bekor qilingan</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Buyurtma</TableHead>
                                <TableHead>Mijoz</TableHead>
                                <TableHead>Mahsulot</TableHead>
                                <TableHead>Summa</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Sana</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredOrders.map((order) => (
                                <TableRow key={order.id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-800"
                                    onClick={() => setSelectedOrder(order)}>
                                    <TableCell className="font-medium">#{order.id}</TableCell>
                                    <TableCell>
                                        <div>
                                            <p className="font-medium">{order.customer_name}</p>
                                            <p className="text-sm text-muted-foreground">{order.phone_number}</p>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            {order.product_image ? (
                                                <img src={order.product_image} alt="" className="w-10 h-10 rounded object-cover" />
                                            ) : (
                                                <div className="w-10 h-10 rounded bg-slate-100 dark:bg-gray-800 flex items-center justify-center">
                                                    <Package className="h-5 w-5 text-muted-foreground" />
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-medium">{order.product_name}</p>
                                                <p className="text-sm text-muted-foreground">{order.quantity} dona</p>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="font-semibold">{formatPrice(order.total_price)}</TableCell>
                                    <TableCell>
                                        <Badge className={cn("text-white", statusColors[order.status])}>
                                            {statusLabels[order.status]}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">{formatDate(order.created_at)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {filteredOrders.length === 0 && (
                        <div className="text-center py-12">
                            <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">Buyurtmalar topilmadi</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Order Detail Modal */}
            <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Buyurtma #{selectedOrder?.id}</DialogTitle>
                    </DialogHeader>
                    {selectedOrder && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                                        <User className="h-4 w-4" /> Mijoz
                                    </p>
                                    <p className="font-medium">{selectedOrder.customer_name}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                                        <Phone className="h-4 w-4" /> Telefon
                                    </p>
                                    <a href={`tel:${selectedOrder.phone_number}`} className="font-medium text-emerald-600">
                                        {selectedOrder.phone_number}
                                    </a>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                                        <Package className="h-4 w-4" /> Mahsulot
                                    </p>
                                    <p className="font-medium">{selectedOrder.product_name}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">Soni</p>
                                    <p className="font-medium">{selectedOrder.quantity} dona</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                                        <Calendar className="h-4 w-4" /> Sana
                                    </p>
                                    <p className="font-medium">{formatDate(selectedOrder.created_at)}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-sm text-muted-foreground">Summa</p>
                                    <p className="font-bold text-lg text-emerald-600">{formatPrice(selectedOrder.total_price)}</p>
                                </div>
                            </div>

                            {selectedOrder.note && (
                                <div className="p-4 bg-slate-50 dark:bg-gray-800 rounded-lg">
                                    <p className="text-sm text-muted-foreground mb-1">Izoh:</p>
                                    <p>{selectedOrder.note}</p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <p className="text-sm text-muted-foreground">Statusni o'zgartirish:</p>
                                <div className="flex flex-wrap gap-2">
                                    {(["pending", "confirmed", "delivered", "cancelled"] as const).map((status) => (
                                        <Button
                                            key={status}
                                            variant={selectedOrder.status === status ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => handleStatusChange(selectedOrder.id, status)}
                                            disabled={isUpdating}
                                            className={cn(selectedOrder.status === status && statusColors[status])}
                                        >
                                            {isUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                                            {statusLabels[status]}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}
